import { createSelector } from "reselect";
import { IconNames } from "@ddm-design-system/icon";
import { IAppState } from "..";
import Routes from "../../routes";
import { getBeverages } from "../beverages/selectors";
import { BeverageTranslation } from "../beverages/types";
import { getComponentStatus } from "../extra10outlets/helpers";
import { ComponentLevelStatus } from "../extra10outlets/types";
import { IX10Device } from "./types";

export interface IX10DeviceRow {
  id: string;
  /** Machine name, or its MAC when it has none. */
  label: string;
  boardSerialNumber: string;
  firmware: string;
  alive: boolean;
  status: Array<{ level: ComponentLevelStatus; icon: IconNames; title?: string }>;
  lastHeardAt: number | null;
  kegLiters: string;
  brand: Array<{ photo: string; title: string }>;
  outletId: string;
  outletName: string;
  outletIsX10: boolean;
  machines: number;
  liveMachines: number;
  country: string;
  distributorName: string;
  /** The X10 device page (Info, Health Monitor, Configuration). */
  link: string;
}

export const getX10DevicesState = (state: IAppState) => state.x10devices;

export const getX10DeviceQuery = (state: IAppState) => state.x10devices.query;

export const getX10DevicesLoading = (state: IAppState) => state.x10devices.loading;

export const getX10DevicesError = (state: IAppState) => state.x10devices.error;

export const getX10DevicesTotal = (state: IAppState) => state.x10devices.result?.totalElements || 0;

const getX10DeviceContent = (state: IAppState) => state.x10devices.result?.content;

export const toDeviceRow = (
  device: IX10Device,
  beverages: BeverageTranslation[]
): IX10DeviceRow => {
  const beverage = device.beverageId ? beverages.find(b => b.id === device.beverageId) : undefined;
  const status = getComponentStatus(device.status);

  return {
    id: device.id,
    label: device.name || device.boardSerialNumber || device.id,
    boardSerialNumber: device.boardSerialNumber || "--",
    firmware: device.firmware || "--",
    alive: device.alive,
    // A live machine with nothing to report shows the same OK icon as the installation view.
    status:
      status.length === 0 && device.alive
        ? [{ level: "SUCCESS", icon: "Ok", title: "OK" }]
        : status,
    lastHeardAt: device.lastHeardAt ? +new Date(device.lastHeardAt) : null,
    kegLiters: device.volumeLeft === null ? "--" : `${(device.volumeLeft / 1000).toFixed(2)}L`,
    brand: beverage ? [{ photo: beverage.logoUrl() || "", title: beverage.name || "" }] : [],
    outletId: device.outlet.id,
    outletName: device.outlet.name || device.outlet.id,
    outletIsX10: device.outlet.x10Location,
    machines: device.outlet.machines,
    liveMachines: device.outlet.liveMachines,
    country: device.outlet.countryCode || "--",
    distributorName: device.outlet.distributorName || "--",
    link: Routes.extra10OutletComponentDetails
      .replace(":id", device.outlet.id)
      .replace(":cid", device.id)
  };
};

export const getX10DeviceRows = createSelector(
  getX10DeviceContent,
  getBeverages,
  (devices: IX10Device[] | undefined, beverages: BeverageTranslation[]) =>
    (devices || []).map(device => toDeviceRow(device, beverages))
);
